import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Save } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ImageCropperModal } from './ImageCropperModal';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  type: 'RANKING' | 'TOURNAMENT';
  initialData?: any;
  onCreate: (data: { name: string; club: string; startDate: string; fee?: number; managerId?: string; imageUrl?: string }) => void;
}

export const CreateCompetitionModal: React.FC<Props> = ({ isOpen, onClose, type, initialData, onCreate }) => {
  const [name, setName] = useState(initialData?.name || '');
  const [clubId, setClubId] = useState(initialData?.clubId || ''); 
  const [startDate, setStartDate] = useState(
    initialData?.config?.startDate 
      ? new Date(initialData.config.startDate).toISOString().split('T')[0] 
      : new Date().toISOString().split('T')[0]
  );
  const [fee, setFee] = useState<string>(initialData?.fee ? parseFloat(String(initialData.fee)).toFixed(2) : '');
  const [managerId, setManagerId] = useState<string>(initialData?.managerId || '');
  const [imageUrl, setImageUrl] = useState<string>(initialData?.imageUrl || '');
  const [cropperImageSrc, setCropperImageSrc] = useState<string | null>(null);
  const [clubs, setClubs] = useState<any[]>([]);
  const { users, currentUser } = useAuth();
  const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

  useEffect(() => {
    const token = localStorage.getItem('tt_auth_token');
    fetch(`${API_URL}/api/clubs`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        if (data && Array.isArray(data)) {
          setClubs(data);
          if (data.length > 0 && !clubId && !initialData?.clubId) {
            setClubId(data[0].id);
          }
        }
      })
      .catch(e => console.error('Erro ao buscar clubes:', e));
  }, []);

  useEffect(() => {
    if (isOpen && initialData) {
      setName(initialData.name || '');
      setClubId(initialData.clubId || '');
      if (initialData.config?.startDate) {
        setStartDate(new Date(initialData.config.startDate).toISOString().split('T')[0]);
      } else {
        setStartDate(new Date().toISOString().split('T')[0]);
      }
      setFee(initialData.fee ? parseFloat(String(initialData.fee)).toFixed(2) : '');
      setManagerId(initialData.managerId || '');
      setImageUrl(initialData.imageUrl || '');
    } else if (isOpen && !initialData) {
      setName('');
      setClubId(clubs.length > 0 ? clubs[0].id : '');
      setStartDate(new Date().toISOString().split('T')[0]);
      setFee('');
      setManagerId('');
      setImageUrl('');
    }
  }, [isOpen, initialData, clubs]);

  if (!isOpen) return null;

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCropperImageSrc(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    
    onCreate({
      name,
      club: clubId,
      startDate,
      fee: fee ? Number(fee) : undefined,
      managerId: managerId || undefined,
      imageUrl: imageUrl || undefined
    });
    
    // Reset state after creation
    if (!initialData) {
      setName('');
      if (clubs.length > 0) setClubId(clubs[0].id);
      setFee('');
      setManagerId('');
      setImageUrl('');
      setStartDate(new Date().toISOString().split('T')[0]);
    }
  };

  return (
    <AnimatePresence>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.4)",
          backdropFilter: "blur(4px)",
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
        }}
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          onClick={(e) => e.stopPropagation()}
          style={{
            width: "100%",
            maxWidth: "500px",
            backgroundColor: "#1A112C",
            backdropFilter: "blur(16px)",
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: "8px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
          }}
        >
          {/* Header */}
          <div
            style={{
              background: "var(--primary-color)",
              padding: "20px 24px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <h2 style={{ color: "#2D1E4B", fontSize: "20px", fontWeight: 700, margin: 0 }}>
                {initialData ? 'Editar' : 'Criar Novo'} {type === 'RANKING' ? 'Ranking' : 'Torneio'}
              </h2>
            </div>
            <button
              onClick={onClose}
              style={{ background: "none", border: "none", color: "#2D1E4B", cursor: "pointer", display: "flex" }}
            >
              <X size={24} />
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit} style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
                Nome do {type === 'RANKING' ? 'Ranking' : 'Torneio'} *
              </label>
              <input 
                type="text" 
                placeholder="Ex: Torneio de Verão 2026"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoFocus
                style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
                Imagem / Logo
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {imageUrl && (
                  <img src={imageUrl} alt="Preview" style={{ width: '48px', height: '48px', borderRadius: '8px', objectFit: 'cover' }} />
                )}
                <input 
                  type="file" 
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white' }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
                Clube / Local
              </label>
              <select 
                value={clubId}
                onChange={e => setClubId(e.target.value)}
                style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white' }}
              >
                {clubs.map((c, i) => (
                  <option key={i} value={c.id} style={{ color: 'black' }}>{c.name}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
                Data de Início
              </label>
              <input 
                type="date" 
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none', boxSizing: 'border-box', colorScheme: 'dark' }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
                Valor da Inscrição (R$)
              </label>
              <input 
                type="number" 
                placeholder="Ex: 50.00"
                value={fee}
                onChange={(e) => setFee(e.target.value)}
                onBlur={(e) => {
                  if (e.target.value) {
                    setFee(parseFloat(e.target.value).toFixed(2));
                  }
                }}
                style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>

            {(currentUser?.role === 'ADMIN' || currentUser?.role === 'GESTOR' || currentUser?.role === 'GESTOR_COMPETICAO') && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
                  Gestor Responsável
                </label>
                <select 
                  value={managerId}
                  onChange={(e) => setManagerId(e.target.value)}
                  style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none', boxSizing: 'border-box' }}
                >
                  <option value="" style={{ color: 'black' }}>Nenhum (Somente Admin)</option>
                  {users.filter(u => u.role === 'GESTOR_COMPETICAO' || u.role === 'ADMIN').map(u => (
                    <option key={u.id} value={u.id} style={{ color: 'black' }}>{u.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '12px' }}>
              <button 
                type="button"
                onClick={onClose}
                style={{ padding: '10px 20px', background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}
              >
                Cancelar
              </button>
              <button 
                type="submit"
                disabled={!name.trim()}
                style={{ padding: '10px 20px', background: 'var(--primary-color)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px', cursor: name.trim() ? 'pointer' : 'not-allowed', opacity: name.trim() ? 1 : 0.5 }}
              >
                {initialData ? <Save size={20} /> : <Plus size={20} />}
                {initialData ? 'Salvar Alterações' : 'Criar Competição'}
              </button>
            </div>
          </form>
        </motion.div>
      </div>

      {cropperImageSrc && (
        <ImageCropperModal
          isOpen={!!cropperImageSrc}
          imageSrc={cropperImageSrc}
          onClose={() => setCropperImageSrc(null)}
          onCropComplete={(croppedBase64) => {
            setImageUrl(croppedBase64);
            setCropperImageSrc(null);
          }}
        />
      )}
    </AnimatePresence>
  );
};
